import type {
  Account,
  BalanceBackfill,
  BriefingLimits,
  BriefingReport,
  EventSyncOutcome,
  MarketEvent,
  MarketEventRequest,
  ScenarioReport,
  CashFlowRelink,
  CommitOutcome,
  CreateCustomInstrumentRequest,
  AllocationDimension,
  AllocationReport,
  BackfillOutcome,
  BenchmarkComparison,
  Quote,
  GapReport,
  Holding,
  Instrument,
  Lot,
  ManualEntry,
  Me,
  OptionStrategy,
  Page,
  OptimiseMethod,
  OptimiseReport,
  AllocationTargetReport,
  GreeksReport,
  IncomeReport,
  Liability,
  LiabilityRequest,
  NetWorthReport,
  OptimiserRun,
  ProblemDetail,
  RegimeReport,
  RiskReport,
  StressReport,
  SavedStatementResponse,
  SavedStatementSummary,
  SectorFillOutcome,
  Snapshot,
  TimeWeightedReturn,
  TransactionRow,
  TokenResponse,
  TrackedUnderlying,
  TradeRehash,
  WizardStepRequest,
  WizardSummary,
  WizardView,
} from './types';

/**
 * Where the tokens live between page loads.
 *
 * <p>Abstracted because the two clients cannot use the same one: the browser has
 * `localStorage`, and React Native must use the Keychain or Keystore rather than AsyncStorage
 * (NFR-SEC2). Neither belongs in this package.
 */
export interface TokenStore {
  read(): StoredTokens | null;
  write(tokens: StoredTokens): void;
  clear(): void;
}

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
  /** Epoch milliseconds. Used only to refresh early, never trusted as authorisation. */
  expiresAt: number;
}

/** An API error carrying the problem+json body, so a caller can show what the server said. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetail;

  constructor(status: number, problem: ProblemDetail) {
    super(problem.detail ?? problem.title ?? `Request failed with ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  /** FR-D3 and FR-E4 both answer an unanswerable-but-well-formed request with a 422. */
  get isUnprocessable(): boolean {
    return this.status === 422;
  }
}

export interface ClientOptions {
  baseUrl: string;
  tokens: TokenStore;
  /** Called when the session is gone for good, so the UI can send the user to the login screen. */
  onSignedOut?: () => void;
}

/** Refresh this long before the access token actually expires, to survive a slow request. */
const REFRESH_MARGIN_MS = 60_000;

export class GreenCandleClient {
  private readonly baseUrl: string;
  private readonly tokens: TokenStore;
  private readonly onSignedOut?: () => void;

  /**
   * One refresh at a time. Several queries firing at once on a stale token would otherwise each
   * start their own refresh, and the backend rotates refresh tokens on use and revokes the whole
   * session when it sees one reused (FR-A1) - so the parallel refreshes would log the user out.
   */
  private refreshing: Promise<StoredTokens> | null = null;

  constructor(options: ClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, '');
    this.tokens = options.tokens;
    this.onSignedOut = options.onSignedOut;
  }

  // --------------------------------------------------------------------------------- auth

  async login(username: string, password: string): Promise<Me> {
    const tokens = await this.request<TokenResponse>('POST', '/api/auth/login', {
      body: { username, password },
      anonymous: true,
    });
    this.store(tokens);
    return this.me();
  }

  async logout(): Promise<void> {
    const stored = this.tokens.read();
    if (stored) {
      try {
        await this.request('POST', '/api/auth/logout', {
          body: { refreshToken: stored.refreshToken },
        });
      } catch {
        // A logout that fails server-side still ends the session on this device. Leaving the
        // tokens in place because the network blipped would be the worse outcome.
      }
    }
    this.tokens.clear();
  }

  me(): Promise<Me> {
    return this.request<Me>('GET', '/api/auth/me');
  }

  isSignedIn(): boolean {
    return this.tokens.read() !== null;
  }

  // ----------------------------------------------------------------------------- portfolio

  listAccounts(): Promise<Account[]> {
    return this.request<Account[]>('GET', '/api/accounts');
  }

  getAccount(id: string): Promise<Account> {
    return this.request<Account>('GET', `/api/accounts/${id}`);
  }

  /** FR-E1 with no date; FR-E2's reconstruction of a past day when one is given. */
  holdings(accountId: string, asOf?: string): Promise<Holding[]> {
    return this.request<Holding[]>('GET', `/api/accounts/${accountId}/holdings`, {
      query: { asOf },
    });
  }

  /** FR-H4. Transactions, newest first, narrowed by type, instrument or date. */
  transactions(
    accountId: string,
    filters: { type?: string; instrumentId?: string; from?: string; to?: string } = {},
  ): Promise<TransactionRow[]> {
    return this.request<TransactionRow[]>('GET', `/api/accounts/${accountId}/transactions`, {
      query: filters,
    });
  }

  /** FR-B5. Enter a transaction of any type by hand. */
  createTransaction(accountId: string, entry: ManualEntry): Promise<TransactionRow> {
    return this.request<TransactionRow>('POST', `/api/accounts/${accountId}/transactions`, {
      body: entry,
    });
  }

  /** FR-B5/H4. Correct one. The server rebuilds the lots the history now implies. */
  updateTransaction(
    accountId: string,
    transactionId: string,
    entry: ManualEntry,
  ): Promise<TransactionRow> {
    return this.request<TransactionRow>(
      'PUT',
      `/api/accounts/${accountId}/transactions/${transactionId}`,
      { body: entry },
    );
  }

  deleteTransaction(accountId: string, transactionId: string): Promise<void> {
    return this.request<void>(
      'DELETE',
      `/api/accounts/${accountId}/transactions/${transactionId}`,
    );
  }

  /** FR-C4. The open lots behind one holding, oldest first. */
  lots(accountId: string, instrumentId: string): Promise<Lot[]> {
    return this.request<Lot[]>(
      'GET',
      `/api/accounts/${accountId}/instruments/${instrumentId}/lots`,
    );
  }

  // ------------------------------------------------------------------------ FR-E2 snapshots

  snapshots(accountId: string, from?: string, to?: string): Promise<Snapshot[]> {
    return this.request<Snapshot[]>('GET', `/api/accounts/${accountId}/snapshots`, {
      query: { from, to },
    });
  }

  /** FR-E5. The account's growth against a benchmark's, both indexed to 100 at the start. */
  benchmark(
    accountId: string,
    symbol?: string,
    from?: string,
    to?: string,
  ): Promise<BenchmarkComparison> {
    return this.request<BenchmarkComparison>(
      'GET',
      `/api/accounts/${accountId}/benchmark`,
      { query: { symbol, from, to } },
    );
  }

  /** FR-D2. Intraday quotes for the account's holdings, for those a source will quote. */
  quotes(accountId: string): Promise<Quote[]> {
    return this.request<Quote[]>('GET', `/api/accounts/${accountId}/quotes`);
  }

  /** FR-H11. The account's option positions, auto-grouped into strategies with a payoff. */
  optionsAnalysis(accountId: string): Promise<OptionStrategy[]> {
    return this.request<OptionStrategy[]>('GET', `/api/accounts/${accountId}/options/analysis`);
  }

  captureSnapshot(accountId: string, date?: string): Promise<Snapshot> {
    return this.request<Snapshot>('POST', `/api/accounts/${accountId}/snapshots`, {
      query: { date },
    });
  }

  snapshotGaps(accountId: string, from?: string, to?: string): Promise<GapReport> {
    return this.request<GapReport>('GET', `/api/accounts/${accountId}/snapshots/gaps`, {
      query: { from, to },
    });
  }

  fillSnapshotGaps(accountId: string, from?: string, to?: string): Promise<BackfillOutcome> {
    return this.request<BackfillOutcome>('POST', `/api/accounts/${accountId}/snapshots/fill-gaps`, {
      query: { from, to },
    });
  }

  backfillSnapshots(accountId: string, from?: string, to?: string): Promise<BackfillOutcome> {
    return this.request<BackfillOutcome>('POST', `/api/accounts/${accountId}/snapshots/backfill`, {
      query: { from, to },
    });
  }

  /** FR-E3. Time-weighted return: how the investments did, not how much was put in. */
  returns(accountId: string, from?: string, to?: string): Promise<TimeWeightedReturn> {
    return this.request<TimeWeightedReturn>('GET', `/api/accounts/${accountId}/returns`, {
      query: { from, to },
    });
  }

  // ----------------------------------------------------------------------- FR-E4 allocation

  /**
   * Across every account, or one of them. `currency` is required only when the accounts in scope
   * do not share a base currency; without it the server answers 400 rather than picking one.
   */
  allocation(params: {
    dimension: AllocationDimension;
    accountId?: string;
    currency?: string;
    asOf?: string;
  }): Promise<AllocationReport> {
    return this.request<AllocationReport>('GET', '/api/portfolio/allocation', { query: params });
  }

  /**
   * FR-G5. Risk of an account's priced holdings, from the Python sidecar (ML-1).
   *
   * Never throws for an unavailable service: the report comes back with `available` false and
   * a reason, because a missing analytics figure must not take a page down (NFR-A3).
   */
  risk(accountId: string, windowDays?: number, confidence?: number): Promise<RiskReport> {
    return this.request<RiskReport>('GET', '/api/portfolio/risk', {
      query: { accountId, windowDays, confidence },
    });
  }

  /**
   * ML-2 / ML-3 / QNT-1. What one optimiser proposes for this account.
   *
   * Never throws for an unavailable service: the report comes back with `available` false and a
   * reason, because an optimiser that cannot answer must not take a page down (NFR-A3).
   *
   * `returnTarget` applies only to CVAR - a floor on the mean daily log return the proposed book
   * must clear, below which the tail is minimised without regard to return. `stressScenario`
   * names one of the crises from {@link stress} and asks what it would have cost this book and
   * the proposed one, which is a different question from whether volatility fell.
   */
  optimise(
    accountId: string,
    method: OptimiseMethod,
    windowDays?: number,
    confidence?: number,
    returnTarget?: number,
    stressScenario?: string,
  ): Promise<OptimiseReport> {
    return this.request<OptimiseReport>('GET', '/api/portfolio/optimise', {
      query: { accountId, method, windowDays, confidence, returnTarget, stressScenario },
    });
  }

  /**
   * ML-3. The book's own current phase, without proposing a target allocation for it.
   *
   * Never throws for an unavailable service: the report comes back with `available` false and
   * a reason, because a missing analytics figure must not take a page down (NFR-A3).
   */
  regime(accountId: string, windowDays?: number): Promise<RegimeReport> {
    return this.request<RegimeReport>('GET', '/api/portfolio/regime', {
      query: { accountId, windowDays },
    });
  }

  /** QNT-2. Four historical crises against this account, all four in one answer. */
  stress(accountId: string, windowDays?: number): Promise<StressReport> {
    return this.request<StressReport>('GET', '/api/portfolio/stress', {
      query: { accountId, windowDays },
    });
  }

  /** QNT-4. The account's greeks, netted per underlying and across the book. */
  /** FR-K9. The account's briefing: exposure, structures, flags and health against its profile. */
  briefing(accountId: string): Promise<BriefingReport> {
    return this.request<BriefingReport>('GET', `/api/accounts/${accountId}/briefing`);
  }

  saveBriefingProfile(accountId: string, limits: BriefingLimits): Promise<BriefingLimits> {
    return this.request<BriefingLimits>('PUT', `/api/accounts/${accountId}/briefing/profile`, {
      body: limits,
    });
  }

  /** Sets the themes named; a blank theme clears that symbol's. Others are left as they are. */
  saveBriefingThemes(accountId: string, themes: Record<string, string>): Promise<Record<string, string>> {
    return this.request<Record<string, string>>(
      'PUT',
      `/api/accounts/${accountId}/briefing/themes`,
      { body: themes },
    );
  }

  /** D-17. Fills the empty sectors of the account's stocks from Finnhub's industries. */
  fillSectors(accountId: string): Promise<SectorFillOutcome> {
    return this.request<SectorFillOutcome>(
      'POST',
      `/api/accounts/${accountId}/briefing/fill-sectors`,
    );
  }

  /** FR-K7. The book repriced under stress and three cases on a quarter's horizon. */
  briefingScenarios(accountId: string): Promise<ScenarioReport> {
    return this.request<ScenarioReport>('GET', `/api/accounts/${accountId}/briefing/scenarios`);
  }

  /** FR-K6. Asks Finnhub for the report dates of everything the account holds or writes on. */
  syncEvents(accountId: string): Promise<EventSyncOutcome> {
    return this.request<EventSyncOutcome>('POST', `/api/accounts/${accountId}/briefing/events/sync`);
  }

  /** A date entered by hand; the same symbol, day and type again restates it. */
  createEvent(accountId: string, event: MarketEventRequest): Promise<MarketEvent> {
    return this.request<MarketEvent>('POST', `/api/accounts/${accountId}/briefing/events`, {
      body: event,
    });
  }

  updateEvent(accountId: string, eventId: string, event: MarketEventRequest): Promise<MarketEvent> {
    return this.request<MarketEvent>(
      'PUT',
      `/api/accounts/${accountId}/briefing/events/${eventId}`,
      { body: event },
    );
  }

  deleteEvent(accountId: string, eventId: string): Promise<void> {
    return this.request<void>('DELETE', `/api/accounts/${accountId}/briefing/events/${eventId}`);
  }

  /** Records option underlyings that have no stock record, and fetches their closes. */
  trackUnderlyings(accountId: string): Promise<TrackedUnderlying[]> {
    return this.request<TrackedUnderlying[]>(
      'POST',
      `/api/accounts/${accountId}/briefing/track-underlyings`,
    );
  }

  greeks(accountId: string): Promise<GreeksReport> {
    return this.request<GreeksReport>('GET', `/api/accounts/${accountId}/greeks`);
  }

  /** UI-1. Target weights per asset class, and how far the account has drifted from them. */
  allocationTargets(accountId: string): Promise<AllocationTargetReport> {
    return this.request<AllocationTargetReport>(
      'GET', `/api/accounts/${accountId}/targets`);
  }

  /** Replaces the whole plan: a set of targets is edited as one thing, not row by row. */
  setAllocationTargets(
    accountId: string,
    targets: Record<string, string>,
  ): Promise<AllocationTargetReport> {
    return this.request<AllocationTargetReport>(
      'PUT', `/api/accounts/${accountId}/targets`, { body: targets });
  }

  /** FR-E7. Trailing twelve months of income, and yield on cost. */
  income(accountId: string): Promise<IncomeReport> {
    return this.request<IncomeReport>('GET', `/api/accounts/${accountId}/income`);
  }

  /** AST-2. Assets minus liabilities, with its own coverage stated. */
  netWorth(accountId: string): Promise<NetWorthReport> {
    return this.request<NetWorthReport>('GET', `/api/accounts/${accountId}/net-worth`);
  }

  /** AST-2. The account's recorded debts. */
  liabilities(accountId: string): Promise<Liability[]> {
    return this.request<Liability[]>('GET', `/api/accounts/${accountId}/liabilities`);
  }

  /**
   * AST-2. Records a debt: a mortgage, a loan, a credit line.
   *
   * <p>The balance is a mark carried forward with the date it was true, not a live figure -
   * nothing here amortises a loan, and a stale balance overstates net worth quietly, which is
   * why `outstandingAsOf` travels beside it everywhere it is shown.
   */
  createLiability(accountId: string, request: LiabilityRequest): Promise<Liability> {
    return this.request<Liability>('POST', `/api/accounts/${accountId}/liabilities`, {
      body: request,
    });
  }

  updateLiability(
    accountId: string,
    liabilityId: string,
    request: LiabilityRequest,
  ): Promise<Liability> {
    return this.request<Liability>(
      'PUT',
      `/api/accounts/${accountId}/liabilities/${liabilityId}`,
      { body: request },
    );
  }

  deleteLiability(accountId: string, liabilityId: string): Promise<void> {
    return this.request<void>(
      'DELETE',
      `/api/accounts/${accountId}/liabilities/${liabilityId}`,
    );
  }

  // -------------------------------------------------------------- audit wizard

  /** Opens the account's audit, or returns the one already open. Safe to call on every visit. */
  startWizard(accountId: string): Promise<WizardView> {
    return this.request<WizardView>('POST', `/api/wizard/start?accountId=${accountId}`);
  }

  /** One step of the open audit. Omit `step` for wherever the reader had got to. */
  wizard(accountId: string, step?: number): Promise<WizardView> {
    const suffix = step === undefined ? '' : `&step=${step}`;
    return this.request<WizardView>('GET', `/api/wizard?accountId=${accountId}${suffix}`);
  }

  submitWizardStep(accountId: string, request: WizardStepRequest): Promise<WizardView> {
    return this.request<WizardView>('POST', `/api/wizard/steps?accountId=${accountId}`, {
      body: request,
    });
  }

  /** Closes the audit and freezes its conclusions. */
  commitWizard(accountId: string): Promise<WizardView> {
    return this.request<WizardView>('POST', `/api/wizard/commit?accountId=${accountId}`);
  }

  /** Throws the open audit away, so the next start begins from nothing. */
  abandonWizard(accountId: string): Promise<void> {
    return this.request<void>('DELETE', `/api/wizard?accountId=${accountId}`);
  }

  wizardHistory(accountId: string): Promise<WizardSummary[]> {
    return this.request<WizardSummary[]>('GET', `/api/wizard/history?accountId=${accountId}`);
  }

  /** INT-3. This account's optimiser runs and whichever of their evaluations exist so far. */
  optimiserRuns(accountId: string): Promise<OptimiserRun[]> {
    return this.request<OptimiserRun[]>('GET', `/api/accounts/${accountId}/optimiser-runs`);
  }

  // ------------------------------------------------------- FR-B1/B2 statement import

  /**
   * Uploads an IBKR Activity Statement, which is parsed and the reading stored.
   *
   * <p>Nothing is written to the portfolio: a statement that does not reconcile never becomes
   * transactions (D-04), and one that does still has to be committed deliberately.
   */
  uploadStatement(file: File): Promise<SavedStatementResponse> {
    const form = new FormData();
    form.append('file', file);
    return this.request<SavedStatementResponse>('POST', '/api/import/ibkr/statements', {
      body: form,
    });
  }

  /** FR-B2. The diff before the confirm: what a commit would write, without writing it. */
  previewCommit(reportId: string, accountId: string): Promise<CommitOutcome> {
    return this.request<CommitOutcome>(
      'POST',
      `/api/import/ibkr/statements/${reportId}/preview-commit`,
      { query: { accountId } },
    );
  }

  /** Writes the statement into the account. Refused server-side unless it reconciles. */
  commitStatement(reportId: string, accountId: string): Promise<CommitOutcome> {
    return this.request<CommitOutcome>(
      'POST',
      `/api/import/ibkr/statements/${reportId}/commit`,
      { query: { accountId } },
    );
  }

  /**
   * FR-E7. Attaches imported cash flows to the holding they were paid on.
   *
   * <p>A repair for statements committed before the importer resolved the instrument: those
   * stored every dividend against nothing, so income could be totalled but never attributed.
   * Idempotent, and worth re-running after importing an older statement - a dividend on a
   * position bought before your imported history had nothing to attach to at the time.
   */
  relinkCashFlows(): Promise<CashFlowRelink> {
    return this.request<CashFlowRelink>('POST', '/api/import/ibkr/relink-cash-flows');
  }

  /**
   * A11. Applies the cash balances and closing prices of statements already imported.
   *
   * <p>Both were discarded at commit until the fix that added them, so a book whose statements
   * all landed beforehand shows a net worth with no cash in it - overstated by any margin loan -
   * until the next statement arrives, which on a monthly cycle can be weeks.
   *
   * <p>Writes balances and prices only: never a transaction, a lot or an instrument. So unlike
   * a re-import it cannot duplicate a fill or disturb a cost basis, and it is safe to repeat.
   */
  backfillBalances(dryRun = false): Promise<BalanceBackfill> {
    return this.request<BalanceBackfill>(
      'POST',
      '/api/import/ibkr/backfill-balances',
      // As a string: the query serialiser takes strings and numbers, and widening it for one
      // flag would touch every caller.
      { query: { dryRun: dryRun ? 'true' : 'false' } },
    );
  }

  /**
   * FR-B11. Moves every imported fill onto the identity that does not depend on whether its
   * statement was read as a PDF or a CSV. Run before the first CSV is committed over PDF history.
   *
   * <p>Writes hashes only - never a transaction, a lot or an amount - and is safe to repeat.
   */
  rehashTrades(dryRun = false): Promise<TradeRehash> {
    return this.request<TradeRehash>(
      'POST',
      '/api/import/ibkr/rehash-trades',
      { query: { dryRun: dryRun ? 'true' : 'false' } },
    );
  }

  savedStatements(): Promise<SavedStatementSummary[]> {
    return this.request<SavedStatementSummary[]>('GET', '/api/import/ibkr/statements');
  }

  savedStatement(id: string): Promise<SavedStatementResponse> {
    return this.request<SavedStatementResponse>('GET', `/api/import/ibkr/statements/${id}`);
  }

  deleteSavedStatement(id: string): Promise<void> {
    return this.request<void>('DELETE', `/api/import/ibkr/statements/${id}`);
  }

  // ---------------------------------------------------------------------------- instruments

  instruments(assetClass?: string, page = 0, size = 50): Promise<Page<Instrument>> {
    return this.request<Page<Instrument>>('GET', '/api/instruments', {
      query: { assetClass, page, size },
    });
  }

  setSector(instrumentId: string, sector: string | null): Promise<Instrument> {
    return this.request<Instrument>('PATCH', `/api/instruments/${instrumentId}/sector`, {
      body: { sector },
    });
  }

  /** AST-1. Registers a property or a private loan so it can be bought and priced by hand. */
  createInstrument(request: CreateCustomInstrumentRequest): Promise<Instrument> {
    return this.request<Instrument>('POST', '/api/instruments', { body: request });
  }

  /**
   * FR-C6. A hand-entered mark for an instrument nothing else prices - refused by the server
   * for anything a feed already prices, so this can never silently shadow a live quote.
   */
  recordManualPrice(
    instrumentId: string,
    date: string,
    price: string,
  ): Promise<{ date: string; close: string; currency: string; source: string }> {
    return this.request('POST', `/api/prices/${instrumentId}/manual`, {
      body: { date, price },
    });
  }

  // -------------------------------------------------------------------------------- plumbing

  private async request<T>(
    method: string,
    path: string,
    options: {
      body?: unknown;
      query?: Record<string, string | number | undefined>;
      anonymous?: boolean;
      retryOnUnauthorized?: boolean;
    } = {},
  ): Promise<T> {
    // Built as a string rather than through `URL`, which requires an absolute address: the web
    // app passes an empty base so that requests are same-origin and Caddy routes them, and
    // `new URL('/api/...')` with no base throws. Mobile will pass an absolute base, and
    // concatenation is correct for both.
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    }
    const query = params.toString();
    const url = `${this.baseUrl}${path}${query ? `?${query}` : ''}`;

    const isForm = options.body instanceof FormData;

    const headers: Record<string, string> = { Accept: 'application/json' };
    // A multipart body must NOT carry a Content-Type set here: the browser has to add one
    // including the boundary it generated, and overwriting it produces a request the server
    // cannot split into parts.
    if (options.body !== undefined && !isForm) {
      headers['Content-Type'] = 'application/json';
    }
    if (!options.anonymous) {
      const token = await this.accessToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, {
      method,
      headers,
      body: options.body === undefined
        ? undefined
        : isForm
          ? (options.body as FormData)
          : JSON.stringify(options.body),
    });

    // A 401 on a token we believed was valid means the server disagrees - it was revoked, or the
    // signing key changed. One refresh attempt, then give up rather than loop.
    if (response.status === 401 && !options.anonymous && options.retryOnUnauthorized !== false) {
      const refreshed = await this.refresh().catch(() => null);
      if (refreshed) {
        return this.request<T>(method, path, { ...options, retryOnUnauthorized: false });
      }
      this.signOut();
      throw new ApiError(401, { title: 'Session expired', detail: 'Please sign in again.' });
    }

    if (!response.ok) {
      throw new ApiError(response.status, await this.problem(response));
    }
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  private async problem(response: Response): Promise<ProblemDetail> {
    try {
      return (await response.json()) as ProblemDetail;
    } catch {
      // A proxy or a crash can produce a non-JSON error. Say what is actually known.
      return { status: response.status, title: response.statusText || 'Request failed' };
    }
  }

  private async accessToken(): Promise<string | null> {
    const stored = this.tokens.read();
    if (!stored) {
      return null;
    }
    if (Date.now() < stored.expiresAt - REFRESH_MARGIN_MS) {
      return stored.accessToken;
    }
    const refreshed = await this.refresh().catch(() => null);
    return refreshed ? refreshed.accessToken : null;
  }

  private refresh(): Promise<StoredTokens> {
    if (this.refreshing) {
      return this.refreshing;
    }
    const stored = this.tokens.read();
    if (!stored) {
      return Promise.reject(new Error('Not signed in'));
    }

    this.refreshing = this.request<TokenResponse>('POST', '/api/auth/refresh', {
      body: { refreshToken: stored.refreshToken },
      anonymous: true,
    })
      .then((tokens) => this.store(tokens))
      .catch((error: unknown) => {
        // The refresh token is single-use and rotating; if it is rejected the session is over.
        this.signOut();
        throw error;
      })
      .finally(() => {
        this.refreshing = null;
      });

    return this.refreshing;
  }

  private store(tokens: TokenResponse): StoredTokens {
    const stored: StoredTokens = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAt: Date.now() + tokens.expiresInSeconds * 1000,
    };
    this.tokens.write(stored);
    return stored;
  }

  private signOut(): void {
    this.tokens.clear();
    this.onSignedOut?.();
  }
}
